import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

import { ObjectType } from '$app-web/types';
import { FatalError } from '$app-web/utils';

import caESLanguage from './ca-es';
import cyGBLanguage from './cy-gb';
import deDELanguage from './de-de';
import enUSLanguage from './en-us';
import esCOLanguage from './es-co';
import esESLanguage from './es-es';
import faIRLanguage from './fa-ir';
import frCaLanguage from './fr-ca';
import heILLanguage from './he-il';
import itITLanguage from './it-it';
import koKRLanguage from './ko-kr';
import nlNLLanguage from './nl-nl';
import ptPTLanguage from './pt-pt';
import slSILanguage from './sl-si';
import itPKLanguage from './ur-pk';
import zhHansLanguage from './zh-hans';
import zuZULanguage from './zu-zu';

abstract class Languages {
    static languageCodes = {
        [enUSLanguage.code]: enUSLanguage,
        [deDELanguage.code]: deDELanguage,
        [faIRLanguage.code]: faIRLanguage,
        [frCaLanguage.code]: frCaLanguage,
        [heILLanguage.code]: heILLanguage,
        [itITLanguage.code]: itITLanguage,
        [koKRLanguage.code]: koKRLanguage,
        [nlNLLanguage.code]: nlNLLanguage,
        [itPKLanguage.code]: itPKLanguage,
        [esESLanguage.code]: esESLanguage,
        [esCOLanguage.code]: esCOLanguage,
        [caESLanguage.code]: caESLanguage,
        [slSILanguage.code]: slSILanguage,
        [zuZULanguage.code]: zuZULanguage,
        [zhHansLanguage.code]: zhHansLanguage,
        [cyGBLanguage.code]: cyGBLanguage,
        [ptPTLanguage.code]: ptPTLanguage,
    };

    static localeToLanguage = {
        [enUSLanguage.locale]: enUSLanguage.code,
        [deDELanguage.locale]: deDELanguage.code,
        [faIRLanguage.locale]: faIRLanguage.code,
        [frCaLanguage.locale]: frCaLanguage.code,
        [heILLanguage.locale]: heILLanguage.code,
        [itITLanguage.locale]: itITLanguage.code,
        [koKRLanguage.locale]: koKRLanguage.code,
        [nlNLLanguage.locale]: nlNLLanguage.code,
        [itPKLanguage.locale]: itPKLanguage.code,
        [esESLanguage.locale]: esESLanguage.code,
        [esCOLanguage.locale]: esCOLanguage.code,
        [caESLanguage.locale]: caESLanguage.code,
        [slSILanguage.locale]: slSILanguage.code,
        [zuZULanguage.locale]: zuZULanguage.code,
        [zhHansLanguage.locale]: zhHansLanguage.code,
        [cyGBLanguage.locale]: cyGBLanguage.code,
        [ptPTLanguage.locale]: ptPTLanguage.code,
    };

    static normalizeSupportedLanguage(languageCode: string) {
        const languageCodeSplit = languageCode.split('-');
        let actualLanguageCode = null;
        if (this.languageExist(languageCode)) {
            if (languageCodeSplit.length === 2) {
                const normalizedLanguageCode = `${languageCodeSplit[0].toLowerCase()}-${languageCodeSplit[1].toUpperCase()}`;
                if (this.languageCodeExist(normalizedLanguageCode)) {
                    actualLanguageCode = normalizedLanguageCode;
                } else {
                    actualLanguageCode = this.localeToLanguage[languageCodeSplit[0]];
                }
            } else if (languageCodeSplit.length === 1) {
                if (this.languageLocaleExist(languageCodeSplit[0])) {
                    actualLanguageCode = this.localeToLanguage[languageCodeSplit[0]];
                }
            }
        }

        return actualLanguageCode;
    }

    static languageExist(language: string) {
        if (!this.languageCodeExist(language) && !this.languageLocaleExist(language)) {
            const languageSplit = language.split('-');
            if (languageSplit.length === 2) {
                return this.languageLocaleExist(languageSplit[0]);
            }
            return false;
        }
        return true;
    }

    static languageCodeExist(languageCode: string) {
        return this.languageCodes[languageCode] != null;
    }

    static languageLocaleExist(languageLocale: string) {
        return this.localeToLanguage[languageLocale] != null;
    }

    static get(languageCode: string) {
        if (this.languageCodeExist(languageCode)) {
            return this.languageCodes[languageCode];
        }
        throw new FatalError(`Language with given code not exist. languageCode: ${languageCode}`);
    }

    static getAll() {
        return this.languageCodes;
    }

    static getAllLanguageCodes() {
        return Object.keys(this.languageCodes);
    }

    static snakeCaseNonTranslationKeys(object: ObjectType, excludedKeys: Array<string> = []) {
        return snakecaseKeys(object, { deep: true, exclude: [...this.getAllLanguageCodes(), ...excludedKeys] });
    }

    static camelCaseNonTranslationKeys(object: ObjectType, excludedKeys: Array<string> = [], stopPaths: Array<string> = []) {
        return camelcaseKeys(object, { deep: true, exclude: [...this.getAllLanguageCodes(), ...excludedKeys], stopPaths });
    }
}

export default Languages;
